// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';

import { RowDataChangedEvent } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import Loader from 'wdna-components/lib/loaders/local/Loader';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { removeTab } from 'wdna-components/lib/tabs';
import { messageError, messageInfo, messageBoxWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { lov, LovDO } from '../../../../configuration/requests';
import { waste, sensor, SensorListDO, WasteInfoDO, WasteContainerDO } from '../../../requests';

import Form from './Form';
import ButtonsForm from '../ButtonsForm';
import RecalibrateModal from './RecalibrateModal';

const MIN_UNLOADS: Map<string, number> = new Map<string, number>([
  ['FOR', 10],
  ['GLA', 10],
  ['PAP', 7],
  ['PET', 7],
  ['PNG', 10],
  ['CRK', 10],
  ['SUW', 10],
]);

const MAX_WEIGHT: Map<string, number> = new Map<string, number>([
  ['120', 60],
  ['240', 100],
  ['800', 320],
  ['1000', 400],
]);

interface Props {
  rowId?: number;
  notify: () => void;
  wasteContainers?: WasteContainerDO[];
}

interface State {
  loading: boolean;
  showGrid?: boolean;

  sensorArray?: SensorListDO[];
  modelWasteArray?: LovDO[];
  typeWasteArray?: LovDO[];
  wasteInfoTable?: WasteInfoDO[];
  isNew: boolean;
  newRecal?: boolean;
  showModal?: boolean;

  data: Partial<WasteContainerDO>;
  info: Partial<WasteInfoDO>;
}

export default class Edit extends React.Component<Props, State> {
  private pid: any;
  private buttons: TableButtonsConfig;
  private mounted: boolean;
  private defaultValues: any;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.defaultValues = {};
    this.buttons = {
      add: true,
      edit: true,
      del: false,
      toggleItem: true,
    };

    this.state = {
      loading: false,
      showGrid: false,
      showModal: false,

      isNew: true,
      newRecal: false,
      sensorArray: [],
      modelWasteArray: [],
      typeWasteArray: [],

      data: {
        date: new Date(),
        minUnload: 0,
        maxWeight: 0,
        minTemp: 1,
        maxTemp: 50,
        active: false
      },

      info: {
        date: new Date(),
        ratio1: 0,
        ratio2: 0,
        ratio3: 0,
        ratio4: 0,
      },
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  };

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = async (): Promise<void> => {
    const { rowId } = this.props || '';
    const isNew: boolean = (rowId === undefined || !!isNaN(rowId));

    this.loadingStart();
    try {
      let wasteInfoTable: WasteInfoDO[] = [];
      let sensorArray: SensorListDO[] = [];
      let wasteRow: Partial<WasteContainerDO> | undefined = this.state.data;
      if (isNew) {
        // TODO: changet to make one call only ??
        const sensorList1: SensorListDO[] = await sensor<SensorListDO[]>('list', undefined, { typeSensorId: '11' }) || [];
        const sensorList2: SensorListDO[] = await sensor<SensorListDO[]>('list', undefined, { typeSensorId: '13' }) || [];
        const sensorList: SensorListDO[] = sensorList1.concat(sensorList2);
        const { wasteContainers } = this.props || [];

        // Get IDs in sensors and not in wasteContainers, and filter the sensors with those IDs
        // That way, the selector will only have type 11 sensors which have not been already created
        const availableIDs: Set<number> = this.getAvailableIDs(sensorList, wasteContainers || []);
        sensorArray = sensorList.filter((sens: SensorListDO) => availableIDs.has(sens.id));
        // eslint-disable-next-line no-nested-ternary
        sensorArray.sort((a: SensorListDO, b: SensorListDO) => ((a.code > b.code) ? 1 : ((b.code > a.code) ? -1 : 0)));
      } else {
        let sensorChosen: SensorListDO | undefined;
        [wasteInfoTable, wasteRow, sensorChosen] = await Promise.all([
          waste<WasteInfoDO[]>('infos', String(rowId)) || [],
          waste<WasteContainerDO>('get', String(rowId)),
          sensor<SensorListDO>('get', String(rowId)),
        ]);

        if (sensorChosen) sensorArray.push(sensorChosen);
        this.buttons.del = wasteInfoTable && wasteInfoTable.length > 1;
      }

      if (this.mounted) {
        const modelWasteArray: LovDO[] = await lov<LovDO[]>('statics', 'model_container') || [];
        const typeWasteArray: LovDO[] = await lov<LovDO[]>('statics', 'type_waste') || [];

        this.setState(
          {
            data: {
              ...wasteRow,
              date: new Date(wasteRow?.date || ''),
            },
            wasteInfoTable,
            sensorArray,
            modelWasteArray,
            typeWasteArray,
            isNew,
          },
          () => {
            this.defaultValues = _.cloneDeep(this.state.data);
          }
        );
      }
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadTable = async (): Promise<void> => {
    const { rowId } = this.props || '';
    this.loadingStart();
    try {
      const wasteInfoTable: WasteInfoDO[] = await waste<WasteInfoDO[]>('infos', String(rowId)) || [];
      this.buttons.del = wasteInfoTable.length > 1;
      this.setState({
        wasteInfoTable,
      });
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public getAvailableIDs = (sensorList: SensorListDO[], wasteContainers: WasteContainerDO[]): Set<number> => {
    const sensorIDs: Set<number> = sensorList.reduce((result: Set<number>, item: SensorListDO) => {
      result.add(item.id);
      return result;
    }, new Set<number>([]));

    const wasteContainersIDs: Set<number> = wasteContainers.reduce((result: Set<number>, item: WasteContainerDO) => {
      result.add(item.id);
      return result;
    }, new Set<number>([]));

    const availableIDs: Set<number> = new Set([...sensorIDs].filter((x: number) => !wasteContainersIDs.has(x)));
    return availableIDs;
  };

  public loadingStart = (): void => {
    this.setState({
      loading: true,
    });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.setState({
      loading: false,
    });
    window.store.dispatch(finishProcess(this.pid));
  };

  public onReset = (): void => {
    this.setState({
      data: {
        ...this.defaultValues,
      }
    });
  };

  public submit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const { isNew, data, info } = this.state;

    if (!this.isValidForm(data)) return;
    if (isNew && !this.isValidInfo(info as WasteInfoDO)) return;

    this.loadingStart();
    try {
      const saved: WasteContainerDO | undefined = await waste<WasteContainerDO>('set', undefined, data);
      if (isNew && saved) {
        info.containerId = saved.id;
        info.date = saved.date;
        await waste('info', 'set', info);
      }
      messageInfo('Success', `Sensor successfully ${data.id ? 'edited' : 'created'} ${data.id}`, 5000);
      const { notify } = this.props || {};
      if (typeof notify === 'function') {
        notify();
      }
      window.store.dispatch(removeTab('WE'));
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public isValidForm = (data: Partial<WasteContainerDO>): boolean => {
    const { id, type, model } = data;
    if (!id) {
      messageError('Error', 'Please select a sensor');
      return false;
    }
    if (!type) {
      messageError('Error', 'Please select the type of the container');
      return false;
    }
    if (!model) {
      messageError('Error', 'Please select the model of the container');
      return false;
    }
    return true;
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | Date | null): void => {
    if (e instanceof Date) {
      this.setState((prevState: State) => ({
        data: {
          ...prevState.data,
          date: e,
        },
      }));
    } else {
      const { name, value, checked, type, dataset } = (e as React.ChangeEvent<HTMLInputElement>)?.target;
      const { handlerType } = dataset || {};
      if (name === 'type') {
        const min: number = MIN_UNLOADS.get(value) || 0;
        this.setState((prevState: State) => ({
          data: {
            ...prevState.data,
            minUnload: min,
          },
        }));
      }
      if (name === 'size') {
        const max: number = MAX_WEIGHT.get(value) || 0;
        this.setState((prevState: State) => ({
          data: {
            ...prevState.data,
            maxWeight: max,
          },
        }));
      }
      if (type === 'checkbox') {
        if (handlerType === 'integer') {
          this.setState((prevState: State) => ({
            data: {
              ...prevState.data,
              [name]: checked ? 1 : 0,
            },
          }));
        } else {
          this.setState((prevState: State) => ({
            data: {
              ...prevState.data,
              [name]: !!checked,
            },
          }));
        }
      } else {
        this.setState((prevState: State) => ({
          data: {
            ...prevState.data,
            [name]: value,
          },
        }));
      }
    }
  };

  public handleChangeInfo = (e: React.ChangeEvent<HTMLInputElement> | Date | null): void => {
    if (e instanceof Date) {
      this.setState((prevState: State) => ({
        info: {
          ...prevState.info,
          date: e,
        },
      }));
    } else {
      const { name, value } = (e as React.ChangeEvent<HTMLInputElement>).target;
      this.setState((prevState: State) => ({
        info: {
          ...prevState.info,
          [name]: value,
        },
      }));
    }
  };

  public rowDataChanged = (_event: RowDataChangedEvent): void => {
    this.setState({
      showGrid: true,
    });
  };

  public handleInfo = (value: WasteInfoDO): void => {
    const rowDate: Date | undefined = value.date;
    this.setState((prevState: State) => ({
      showModal: true,
      newRecal: (rowDate === undefined),
      info: {
        containerId: prevState.data.id,
        date: rowDate || new Date(),
        ratio1: value.ratio1 || 0,
        ratio2: value.ratio2 || 0,
        ratio3: value.ratio3 || 0,
        ratio4: value.ratio4 || 0,
      }
    }));
  };

  public handleCancel = (_e?: React.MouseEvent<HTMLButtonElement>): void => {
    this.setState({
      showModal: false,
    });
  };

  public handleInfoDelete = (value: WasteInfoDO): void => {
    messageBoxWarning('Delete', 'Do you want to delete this register?', (): void => {
      const request: Record<string, any> = {
        containerId: value.containerId,
        date: new Date(value.date)
      };

      this.loadingStart();
      waste('info', 'del', request)
        ?.then(() => {
          messageInfo('Success', 'Register successfully deleted', 5000);
          this.loadTable().catch(() => { /* pass */ });
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  public handleInfoSave = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    const { info } = this.state;

    if (!this.isValidInfo(info as WasteInfoDO)) return;

    this.loadingStart();
    this.loadingStart();
    waste('info', 'set', info)
      ?.then(() => {
        messageInfo('Success', `Recalibration successful for container ${info.containerId}`, 5000);
        this.loadTable().catch(() => { /* pass */ });
        this.handleCancel();
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public isValidInfo = (data: WasteInfoDO): boolean => {
    const { ratio1, ratio2, ratio3, ratio4 } = data;
    if (isNaN(ratio1) || isNaN(ratio2) || isNaN(ratio3) || isNaN(ratio4)) {
      messageError('Error', 'Please enter valid parameters');
      return false;
    }
    /*
    if (ratio1 >= 100 || ratio2 >= 100 || ratio3 >= 100 || ratio4 >= 100) {
      messageError('Error', 'The ratios of the wheels must be below 100%');
      return false;
    }
    */
    return true;
  };

  public autoFormMin = (data: WasteContainerDO): number => {
    const { type } = data;
    return MIN_UNLOADS.get(type) || 0;
  };

  public autoFormMax = (data: WasteContainerDO): number => {
    const { size } = data;
    return MAX_WEIGHT.get(String(size)) || 0;
  };

  public render(): JSX.Element {
    const { loading, data, isNew, showGrid, wasteInfoTable } = this.state;

    const columnsToHide: string[] = ['containerId'];
    const columnAliases: Record<string, string> = {
      date: 'Date',
      ratio1: 'Ratio Wheel-1',
      ratio2: 'Ratio Wheel-2',
      ratio3: 'Ratio Wheel-3',
      ratio4: 'Ratio Wheel-4',
    };
    const columnStyles: Record<string, any> = {
      date: { textAlign: 'center' },
      ratio1: { textAlign: 'right' },
      ratio2: { textAlign: 'right' },
      ratio3: { textAlign: 'right' },
      ratio4: { textAlign: 'right' },
      actionButtons: { textAlign: 'center' },
    };

    return (
      <div id="content" className="content">
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12 col-md-12 col-lg-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                colorbutton={false}
                deletebutton={false}
                custombutton={false}
                fullscreenbutton={false}
              >
                <div className="widget-body">
                  <Loader show={loading} overlay={false} />
                  <UiValidate>
                    <form
                      className="smart-form"
                      onSubmit={(e: React.FormEvent<HTMLFormElement>): void => { this.submit(e).catch(() => { /* pass */ }); }}
                      method="post"
                      encType="multipart/form-data"
                    >
                      <header>
                        <h2>
                          <span className="widget-icon" style={{ padding: '8px' }}>
                            <i className="fa fa-edit" />
                          </span>
                          Waste-Container {isNew ? 'New' : `Edit: ${data.id}`}
                        </h2>
                      </header>
                      <Form
                        duplicate={false}
                        onChange={this.handleChange}
                        onChangeInfo={this.handleChangeInfo}
                        formValues={this.state}
                        onAutoFormMin={this.autoFormMin}
                        onAutoFormMax={this.autoFormMax}
                      />
                      <ButtonsForm formValues={this.state} onReset={this.onReset} />
                    </form>
                  </UiValidate>
                </div>
              </JarvisWidget>
              {!this.state.isNew && (
                <div className="infoWaste">
                  <RecalibrateModal
                    show={this.state.showModal}
                    title={`Recalibrate container ${data.id || ''}`}
                    onCancel={this.handleCancel}
                    onSave={this.handleInfoSave}
                    onChange={this.handleChangeInfo}
                    formValues={this.state}
                    className="infoWaste"
                  />
                  <div id="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
                    <Table
                      data={wasteInfoTable || []}
                      hiddenCols={columnsToHide}
                      alias={columnAliases}
                      customCellStyles={columnStyles}
                      buttons={this.buttons}
                      loading={this.state.loading}
                      rowDataChanged={this.rowDataChanged}
                      onAdd={this.handleInfo}
                      onEdit={this.handleInfo}
                      onDelete={this.handleInfoDelete}
                      height="calc(40vh)"
                    />
                  </div>
                </div>
              )}
            </article>
          </div>
        </WidgetGrid>
      </div>
    );
  }
}
